import { RiskModel, RisksId } from "./risk";
import { PropertyTypeId } from "./propertyType";
import React from "react";
import { RisksNetworkResponseModel, RisksNetworkTypes } from "./risksNetwork";
import { RequestStatus, DefaultRequestStatus } from "../services/services/requestStatus";
import { makeObservable, observable } from "mobx";
import { CalculationResponseModel } from "./calculation";

export enum PackageId {
    mini = "mini",
    midi = "midi",
    maxi = "maxi"
}

interface PolicyDuration {
    months12: RisksNetworkTypes;
    months36: RisksNetworkTypes;
}

export interface PackageModel {
    id: PackageId;
    displayName: string;
    displayDesc: string;

    riskNetworkRequestStatus: RequestStatus;
    calculationRequestStatus: RequestStatus;

    calculationResponse: CalculationResponseModel | undefined | null;

    risks: Map<RisksId, RiskModel>;
    policyDuration: PolicyDuration;
    requireRecalculate: boolean;
}

export interface DefaultPackageModelArgs {
    id: PackageId;
    displayName: string;
    displayDesc: string;
    risks: Map<RisksId, RiskModel>;
    policyDuration: PolicyDuration;
}

class DefaultPackageModel implements PackageModel {
    @observable id: PackageId;
    @observable displayName: string;
    @observable displayDesc: string;
    @observable riskNetworkRequestStatus: RequestStatus;
    @observable calculationRequestStatus: RequestStatus;
    @observable calculationResponse: CalculationResponseModel | undefined | null;
    @observable risks: Map<RisksId, RiskModel>;
    @observable policyDuration: PolicyDuration;
    @observable requireRecalculate: boolean;

    constructor(args: DefaultPackageModelArgs) {
        makeObservable(this);

        this.id = args.id;
        this.displayName = args.displayName;
        this.displayDesc = args.displayDesc;
        this.riskNetworkRequestStatus = new DefaultRequestStatus();
        this.calculationRequestStatus = new DefaultRequestStatus();
        this.risks = args.risks;
        this.policyDuration = args.policyDuration;
        this.requireRecalculate = false;
    }
}

export class Package {
    public allPackages: Map<PackageId, PackageModel>;

    public constructor(propertyType: PropertyTypeId) {
        this.allPackages = new Map();
        const detachedHouseOrFlatUnderConstruction =
            propertyType === PropertyTypeId.detachedHouseUnderConstructionClosedState ||
            propertyType === PropertyTypeId.detachedHouseUnderConstructionOpenState ||
            propertyType === PropertyTypeId.flatUnderConstructionClosedState ||
            propertyType === PropertyTypeId.flatUnderConstructionOpenState;

        this.allPackages.set(PackageId.mini, new DefaultPackageModel({
            id: PackageId.mini,
            displayName: "Podstawowy",
            displayDesc: "Ryzyka nazwane",
            risks: this.getRisksForPackageMini(propertyType),
            policyDuration: Package.getPolicyDuration(propertyType)
        }));

        this.allPackages.set(PackageId.midi, new DefaultPackageModel({
            id: PackageId.midi,
            displayName: detachedHouseOrFlatUnderConstruction ? "Standardowy" : "Rozszerzony",
            displayDesc: "Ryzyka nazwane",
            risks: this.getRisksForPackageMidi(propertyType),
            policyDuration: Package.getPolicyDuration(propertyType)
        }));

        this.allPackages.set(PackageId.maxi, new DefaultPackageModel({
            id: PackageId.maxi,
            displayName: detachedHouseOrFlatUnderConstruction ? "Rozszerzony" : "All Risk",
            displayDesc: detachedHouseOrFlatUnderConstruction ? "Ryzyka nazwane" : "Od wszystkich ryzyk",
            risks: this.getRisksForPackageMaxi(propertyType),
            policyDuration: Package.getPolicyDuration(propertyType)
        }));
    }

    private getContextHelp(id: string, available: boolean, additionalInfo?: string) {
        return {
            id,
            available,
            additionalInfo: additionalInfo ?
                <div>
                    {additionalInfo!.split("\n").map((line, index) =>
                        <div key={`${id}_${index}`}>{line}</div>)
                    }
                </div> :
                ""
        };
    }

    private static getPolicyDuration(propertyType: PropertyTypeId): PolicyDuration {
        switch (propertyType) {
            case PropertyTypeId.detachedHouseUnderConstructionClosedState:
            case PropertyTypeId.detachedHouseUnderConstructionOpenState:
            case PropertyTypeId.flatUnderConstructionClosedState:
            case PropertyTypeId.flatUnderConstructionOpenState:
                return { months12: RisksNetworkTypes.available, months36: RisksNetworkTypes.notAvailable };
            default:
                return { months12: RisksNetworkTypes.available, months36: RisksNetworkTypes.available };
        }
    }

    private getRisksForPackageMini(propertyType: PropertyTypeId) {
        switch (propertyType) {
            case PropertyTypeId.detachedHouseUnderConstructionOpenState:
            case PropertyTypeId.flatUnderConstructionOpenState:
                return this.getRisksForPackageMiniForUnderConstructionOpenState();
            case PropertyTypeId.detachedHouseUnderConstructionClosedState:
            case PropertyTypeId.flatUnderConstructionClosedState:
                return this.getRisksForPackageMiniForUnderConstructionClosedState();
            default:
                return this.getRisksForPackageMiniDefault();
        }
    }

    private getRisksForPackageMidi(propertyType: PropertyTypeId) {
        switch (propertyType) {
            case PropertyTypeId.detachedHouseUnderConstructionOpenState:
            case PropertyTypeId.flatUnderConstructionOpenState:
                return this.getRisksForPackageMidiForUnderConstructionOpenState();
            case PropertyTypeId.detachedHouseUnderConstructionClosedState:
            case PropertyTypeId.flatUnderConstructionClosedState:
                return this.getRisksForPackageMidiForUnderConstructionClosedState();
            default:
                return this.getRisksForPackageMidiDefault();
        }
    }

    private getRisksForPackageMaxi(propertyType: PropertyTypeId) {
        switch (propertyType) {
            case PropertyTypeId.detachedHouseUnderConstructionOpenState:
            case PropertyTypeId.flatUnderConstructionOpenState:
                return this.getRisksForPackageMaxiForUnderConstructionOpenState();
            case PropertyTypeId.detachedHouseUnderConstructionClosedState:
            case PropertyTypeId.flatUnderConstructionClosedState:
                return this.getRisksForPackageMaxiForUnderConstructionClosedState();
            default:
                return this.getRisksForPackageMaxiDefault();
        }
    }

    private getRisksForPackageDefault() {
        const risks: Map<RisksId, RiskModel> = new Map();

        risks.set(RisksId.detachedHouseOrFlatWithFixedElements, {
            getInsuranceSumFromResponse: (response) => response.coverages.houseOrFlatWithFixedElements?.insuranceSum,
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.fireAndRandomEvents!.detachedHouseOrFlat,
            getInsuranceSumFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.detachedHouseOrFlatWithFixedElements?.insuranceSumInCents
        });
        risks.set(RisksId.onlyFixedElements, {
            getInsuranceSumFromResponse: (response) => response.coverages.onlyFixedElements?.insuranceSum,
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.fireAndRandomEvents.onlyFixedElements,
            getInsuranceSumFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.onlyFixedElements?.insuranceSumInCents
        });
        risks.set(RisksId.movables, {
            getInsuranceSumFromResponse: (response) => response.coverages.movables?.insuranceSum,
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.fireAndRandomEvents.movables,
            getInsuranceSumFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.movables?.insuranceSumInCents
        });
        risks.set(RisksId.burglaryAndTheftMovables, {
            getInsuranceSumFromResponse: (response) => response.coverages.burglaryAndTheftMovables?.insuranceSum,
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.burglaryAndTheft.movables,
            getInsuranceSumFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.burglaryAndTheftMovables?.insuranceSumInCents
        });
        risks.set(RisksId.fixedElementsWithBurglaryAndTheft, {
            getInsuranceSumFromResponse: (response) => response.coverages.fixedElementsWithBurglaryAndTheft?.insuranceSum,
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.burglaryAndTheft.fixedElements,
            getInsuranceSumFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.fixedElementsWithBurglaryAndTheft?.insuranceSumInCents
        });
        risks.set(RisksId.theft, {
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.additionalRisks.theft,
            getAvailableFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.additionalRisks?.theft
        });
        risks.set(RisksId.assistance, {
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.assistance,
            getAvailableFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.assistance
        });
        risks.set(RisksId.risksInOwu, {});
        risks.set(RisksId.riskMode, {});
        risks.set(RisksId.devastation, {
            getInsuranceSumFromResponse: (response) => response.coverages.additionalRisks.devastation?.insuranceSum,
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.additionalRisks.devastation,
            getAvailableFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.additionalRisks?.devastation,
            getInsuranceSumFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.additionalRisks?.devastation?.insuranceSumInCents
        });
        risks.set(RisksId.searchingAndRemovingCauseOfDamage, {
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.additionalRisks.searchingAndRemovingCauseOfDamage,
            getAvailableFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.additionalRisks?.searchingAndRemovingCauseOfDamage
        });
        risks.set(RisksId.windowpanes, {
            getInsuranceSumFromResponse: (response) => response.coverages.additionalRisks?.windowpanes?.insuranceSum,
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.additionalRisks.windowpanes,
            getInsuranceSumFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.additionalRisks?.windowpanes?.insuranceSumInCents
        });
        risks.set(RisksId.surges, {
            getInsuranceSumFromResponse: (response) => response.coverages.additionalRisks?.surges?.insuranceSum,
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.additionalRisks.surges,
            getInsuranceSumFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.additionalRisks?.surges?.insuranceSumInCents
        });
        risks.set(RisksId.flood, {
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.flood,
            getAvailableFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.flood
        });

        return risks;
    }

    private getRisksForPackageMiniDefault() {
        const risks: Map<RisksId, RiskModel> = new Map();
        const defaultMap = this.getRisksForPackageDefault();

        risks.set(RisksId.detachedHouseOrFlatWithFixedElements, {
            ...defaultMap.get(RisksId.detachedHouseOrFlatWithFixedElements),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.detachedHouseOrFlatWithFixedElements}1`, true)
            ]
        });
        risks.set(RisksId.onlyFixedElements, {
            ...defaultMap.get(RisksId.onlyFixedElements),
            available: false,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.onlyFixedElements}1`, true)
            ]
        });
        risks.set(RisksId.assistance, {
            ...defaultMap.get(RisksId.assistance),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.assistance}1`, true),
                this.getContextHelp(`${RisksId.assistance}2`, true),
                this.getContextHelp(`${RisksId.assistance}3`, true),
                this.getContextHelp(`${RisksId.assistance}4`, true),
                this.getContextHelp(`${RisksId.assistance}5`, true),
                this.getContextHelp(`${RisksId.assistance}6`, true)
            ]
        });
        risks.set(RisksId.risksInOwu, {
            ...defaultMap.get(RisksId.risksInOwu),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.risksInOwu}1`, true),
                this.getContextHelp(`${RisksId.risksInOwu}2`, true),
                this.getContextHelp(`${RisksId.risksInOwu}3`, true),
                this.getContextHelp(`${RisksId.risksInOwu}4`, false, "5 000 zł"),
                this.getContextHelp(`${RisksId.risksInOwu}5`, true, "1 000 zł")
            ]
        });
        risks.set(RisksId.devastation, {
            ...defaultMap.get(RisksId.devastation),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.devastation}1`, true),
                this.getContextHelp(`${RisksId.devastation}2`, true),
                this.getContextHelp(`${RisksId.devastation}3`, true)
            ]
        });
        risks.set(RisksId.searchingAndRemovingCauseOfDamage, {
            ...defaultMap.get(RisksId.searchingAndRemovingCauseOfDamage),
            available: true,
            editable: false
        });
        risks.set(RisksId.flood, {
            ...defaultMap.get(RisksId.flood),
            available: false,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.flood}1`, true)
            ]
        });

        return risks;
    }

    private getRisksForPackageMidiDefault() {
        const risks: Map<RisksId, RiskModel> = new Map();
        const defaultMap = this.getRisksForPackageDefault();

        risks.set(RisksId.detachedHouseOrFlatWithFixedElements, {
            ...defaultMap.get(RisksId.detachedHouseOrFlatWithFixedElements),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.detachedHouseOrFlatWithFixedElements}1`, true)
            ]
        });
        risks.set(RisksId.onlyFixedElements, {
            ...defaultMap.get(RisksId.onlyFixedElements),
            available: false,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.onlyFixedElements}1`, true)
            ]
        });
        risks.set(RisksId.movables, {
            ...defaultMap.get(RisksId.movables),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.movables}1`, true),
                this.getContextHelp(`${RisksId.movables}2`, true, "1 000 zł"),
                this.getContextHelp(`${RisksId.movables}3`, true),
                this.getContextHelp(`${RisksId.movables}4`, true, "20% SU"),
                this.getContextHelp(`${RisksId.movables}5`, true, "5% SU\nnie więcej niż 2 000 zł"),
                this.getContextHelp(`${RisksId.movables}6`, true, "20% SU\nnie więcej niż 10 000 zł"),
                this.getContextHelp(`${RisksId.movables}7`, true, "2 000 zł"),
                this.getContextHelp(`${RisksId.movables}8`, true, "10% SU\nnie więcej niż 5 000 zł"),
                this.getContextHelp(`${RisksId.movables}9`, true, "1 000 zł"),
                this.getContextHelp(`${RisksId.movables}11`, true, "40% SU"),
                this.getContextHelp(`${RisksId.movables}12`, true, "SU 3 000 zł")

            ]
        });
        risks.set(RisksId.burglaryAndTheftMovables, {
            ...defaultMap.get(RisksId.burglaryAndTheftMovables),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}1`, true),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}2`, true),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}3`, true, "1 000 zł"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}4`, true),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}5`, true, "20 % SU"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}6`, true, "10% SU\nnie więcej niż 2 000 zł"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}7`, true, "20% SU\nnie więcej niż 10 000 zł"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}8`, true, "2 000 zł"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}9`, true, "10% SU\nnie więcej niż 5 000 zł"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}10`, true, "1 000 zł"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}11`, true, "40%"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}12`, true, "SU 3 000 zł")

            ]
        });
        risks.set(RisksId.fixedElementsWithBurglaryAndTheft, {
            ...defaultMap.get(RisksId.fixedElementsWithBurglaryAndTheft),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.fixedElementsWithBurglaryAndTheft}1`, true),
                this.getContextHelp(`${RisksId.fixedElementsWithBurglaryAndTheft}2`, true)
            ]
        });
        risks.set(RisksId.theft, {
            ...defaultMap.get(RisksId.theft),
            available: false,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.theft}1`, true, "5 000 zł")
            ]
        });
        risks.set(RisksId.assistance, {
            ...defaultMap.get(RisksId.assistance),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.assistance}1`, true),
                this.getContextHelp(`${RisksId.assistance}2`, true),
                this.getContextHelp(`${RisksId.assistance}3`, true),
                this.getContextHelp(`${RisksId.assistance}4`, true),
                this.getContextHelp(`${RisksId.assistance}5`, true),
                this.getContextHelp(`${RisksId.assistance}6`, true)
            ]
        });
        risks.set(RisksId.risksInOwu, {
            ...defaultMap.get(RisksId.risksInOwu),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.risksInOwu}1`, true),
                this.getContextHelp(`${RisksId.risksInOwu}2`, true),
                this.getContextHelp(`${RisksId.risksInOwu}3`, true),
                this.getContextHelp(`${RisksId.risksInOwu}4`, false, "5 000 zł"),
                this.getContextHelp(`${RisksId.risksInOwu}5`, true, "1 000 zł")
            ]
        });
        risks.set(RisksId.devastation, {
            ...defaultMap.get(RisksId.devastation),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.devastation}1`, true),
                this.getContextHelp(`${RisksId.devastation}2`, true),
                this.getContextHelp(`${RisksId.devastation}3`, true)
            ]
        });
        risks.set(RisksId.searchingAndRemovingCauseOfDamage, {
            ...defaultMap.get(RisksId.searchingAndRemovingCauseOfDamage),
            available: true,
            editable: false
        });
        risks.set(RisksId.surges, {
            ...defaultMap.get(RisksId.surges),
            available: false,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.surges}1`, true),
                this.getContextHelp(`${RisksId.surges}2`, false, "5 000 zł")
            ]
        });
        risks.set(RisksId.windowpanes, {
            ...defaultMap.get(RisksId.windowpanes),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.windowpanes}1`, true)
            ]
        });
        risks.set(RisksId.flood, {
            ...defaultMap.get(RisksId.flood),
            available: false,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.flood}1`, true)
            ]
        });

        return risks;
    }

    private getRisksForPackageMaxiDefault() {
        const risks: Map<RisksId, RiskModel> = new Map();
        const defaultMap = this.getRisksForPackageDefault();

        risks.set(RisksId.detachedHouseOrFlatWithFixedElements, {
            ...defaultMap.get(RisksId.detachedHouseOrFlatWithFixedElements),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.detachedHouseOrFlatWithFixedElements}1`, true)
            ]
        });
        risks.set(RisksId.onlyFixedElements, {
            ...defaultMap.get(RisksId.onlyFixedElements),
            available: false,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.onlyFixedElements}1`, true)
            ]
        });
        risks.set(RisksId.movables, {
            ...defaultMap.get(RisksId.movables),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.movables}1`, true),
                this.getContextHelp(`${RisksId.movables}2`, true, "2 000 zł"),
                this.getContextHelp(`${RisksId.movables}3`, true),
                this.getContextHelp(`${RisksId.movables}4`, true, "30% SU"),
                this.getContextHelp(`${RisksId.movables}5`, true, "10% SU\nnie więcej niż 5 000 zł"),
                this.getContextHelp(`${RisksId.movables}6`, true, "40% SU\nnie więcej niż 20 000 zł"),
                this.getContextHelp(`${RisksId.movables}7`, true, "4 000 zł"),
                this.getContextHelp(`${RisksId.movables}8`, true, "20%\nnie więcej niż 10 000 zł"),
                this.getContextHelp(`${RisksId.movables}9`, true, "2 000 zł"),
                this.getContextHelp(`${RisksId.movables}10`, true, "1 000 zł"),
                this.getContextHelp(`${RisksId.movables}11`, true, "60%"),
                this.getContextHelp(`${RisksId.movables}12`, true, "SU 3 000 zł")

            ]
        });
        risks.set(RisksId.burglaryAndTheftMovables, {
            ...defaultMap.get(RisksId.burglaryAndTheftMovables),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}1`, true),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}2`, true),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}3`, true, "2 000 zł"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}4`, true),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}5`, true, "30% SU"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}6`, true, "15% SU\nnie więcej niż 5 000 zł"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}7`, true, "40%\nnie więcej niż 20 000 zł"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}8`, true, "4 000 zł"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}9`, true, "20%\nnie więcej niż 10 000 zł"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}10`, true, "2 000 zł"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}11`, true, "60%"),
                this.getContextHelp(`${RisksId.burglaryAndTheftMovables}12`, true, "SU 3 000 zł")
            ]
        });
        risks.set(RisksId.fixedElementsWithBurglaryAndTheft, {
            ...defaultMap.get(RisksId.fixedElementsWithBurglaryAndTheft),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.fixedElementsWithBurglaryAndTheft}1`, true),
                this.getContextHelp(`${RisksId.fixedElementsWithBurglaryAndTheft}2`, true)
            ]
        });
        risks.set(RisksId.theft, {
            ...defaultMap.get(RisksId.theft),
            available: false,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.theft}1`, true, "10 000zł"),
                this.getContextHelp(`${RisksId.theft}2`, true, "2 000 zł")
            ]
        });
        risks.set(RisksId.assistance, {
            ...defaultMap.get(RisksId.assistance),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.assistance}1`, true),
                this.getContextHelp(`${RisksId.assistance}2`, true),
                this.getContextHelp(`${RisksId.assistance}3`, true),
                this.getContextHelp(`${RisksId.assistance}4`, true),
                this.getContextHelp(`${RisksId.assistance}5`, true),
                this.getContextHelp(`${RisksId.assistance}6`, true)
            ]
        });
        risks.set(RisksId.risksInOwu, {
            ...defaultMap.get(RisksId.risksInOwu),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.risksInOwu}1`, true),
                this.getContextHelp(`${RisksId.risksInOwu}2`, true),
                this.getContextHelp(`${RisksId.risksInOwu}3`, true),
                this.getContextHelp(`${RisksId.risksInOwu}4`, false, "10 000 zł"),
                this.getContextHelp(`${RisksId.risksInOwu}5`, true, "2 000 zł"),
                this.getContextHelp(`${RisksId.risksInOwu}6`, true, "20 000 zł"),
                this.getContextHelp(`${RisksId.risksInOwu}7`, true, "3 000 zł"),
                this.getContextHelp(`${RisksId.risksInOwu}8`, true)
            ]
        });
        risks.set(RisksId.riskMode, {
            ...defaultMap.get(RisksId.riskMode),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.riskMode}1`, true)
            ]
        });
        risks.set(RisksId.devastation, {
            ...defaultMap.get(RisksId.devastation),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.devastation}1`, true),
                this.getContextHelp(`${RisksId.devastation}2`, true),
                this.getContextHelp(`${RisksId.devastation}3`, true)
            ]
        });
        risks.set(RisksId.searchingAndRemovingCauseOfDamage, {
            ...defaultMap.get(RisksId.searchingAndRemovingCauseOfDamage),
            available: true,
            editable: false
        });
        risks.set(RisksId.windowpanes, {
            ...defaultMap.get(RisksId.windowpanes),
            available: true,
            editable: false,
            hideInsuranceSumFromResponse: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.windowpanes}1`, true)
            ]
        });
        risks.set(RisksId.surges, {
            ...defaultMap.get(RisksId.surges),
            available: true,
            editable: false,
            hideInsuranceSumFromResponse: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.surges}1`, true),
                this.getContextHelp(`${RisksId.surges}2`, false, "10 000 zł")
            ]
        });
        risks.set(RisksId.flood, {
            ...defaultMap.get(RisksId.flood),
            available: false,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.flood}1`, true)
            ]
        });

        return risks;
    }

    private getRisksForPackageForUnderConstructionOpenState() {
        const risks: Map<RisksId, RiskModel> = new Map();

        risks.set(RisksId.detachedHouseOrFlatWithFixedElements, {
            getInsuranceSumFromResponse: (response) => response.coverages.houseOrFlatWithFixedElements?.insuranceSum,
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.fireAndRandomEvents.detachedHouseOrFlat,
            getInsuranceSumFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.detachedHouseOrFlatWithFixedElements?.insuranceSumInCents
        });
        risks.set(RisksId.risksInOwu, {});
        risks.set(RisksId.flood, {
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.flood,
            getAvailableFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.flood
        });

        return risks;
    }

    private getRisksForPackageMiniForUnderConstructionOpenState() {
        const risks: Map<RisksId, RiskModel> = new Map();
        const defaultMap = this.getRisksForPackageForUnderConstructionOpenState();

        risks.set(RisksId.detachedHouseOrFlatWithFixedElements, {
            ...defaultMap.get(RisksId.detachedHouseOrFlatWithFixedElements),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.detachedHouseOrFlatWithFixedElements}1`, true)
            ]
        });
        risks.set(RisksId.risksInOwu, {
            ...defaultMap.get(RisksId.risksInOwu),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.risksInOwu}1`, true),
                this.getContextHelp(`${RisksId.risksInOwu}2`, true)
            ]
        });
        risks.set(RisksId.flood, {
            ...defaultMap.get(RisksId.flood),
            available: false,
            editable: true
        });

        return risks;
    }

    private getRisksForPackageMidiForUnderConstructionOpenState() {
        const risks: Map<RisksId, RiskModel> = new Map();
        const defaultMap = this.getRisksForPackageForUnderConstructionOpenState();

        risks.set(RisksId.detachedHouseOrFlatWithFixedElements, {
            ...defaultMap.get(RisksId.detachedHouseOrFlatWithFixedElements),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.detachedHouseOrFlatWithFixedElements}1`, true)
            ]
        });
        risks.set(RisksId.risksInOwu, {
            ...defaultMap.get(RisksId.risksInOwu),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.risksInOwu}1`, true),
                this.getContextHelp(`${RisksId.risksInOwu}2`, true)
            ]
        });
        risks.set(RisksId.flood, {
            ...defaultMap.get(RisksId.flood),
            available: false,
            editable: true
        });

        return risks;
    }

    private getRisksForPackageMaxiForUnderConstructionOpenState() {
        const risks: Map<RisksId, RiskModel> = new Map();
        const defaultMap = this.getRisksForPackageForUnderConstructionOpenState();

        risks.set(RisksId.detachedHouseOrFlatWithFixedElements, {
            ...defaultMap.get(RisksId.detachedHouseOrFlatWithFixedElements),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.detachedHouseOrFlatWithFixedElements}1`, true)
            ]
        });
        risks.set(RisksId.risksInOwu, {
            ...defaultMap.get(RisksId.risksInOwu),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.risksInOwu}1`, true),
                this.getContextHelp(`${RisksId.risksInOwu}2`, true)
            ]
        });
        risks.set(RisksId.flood, {
            ...defaultMap.get(RisksId.flood),
            available: false,
            editable: true
        });

        return risks;
    }

    private getRisksForPackageForUnderConstructionClosedState() {
        const risks: Map<RisksId, RiskModel> = new Map();

        risks.set(RisksId.detachedHouseOrFlatWithFixedElements, {
            getInsuranceSumFromResponse: (response) => response.coverages.houseOrFlatWithFixedElements?.insuranceSum,
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.fireAndRandomEvents.detachedHouseOrFlat,
            getInsuranceSumFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.detachedHouseOrFlatWithFixedElements?.insuranceSumInCents
        });
        risks.set(RisksId.buildingToolsAndMaterials, {
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.additionalRisks.buildingToolsAndMaterials,
            getAvailableFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.additionalRisks?.buildingToolsAndMaterials
        });
        risks.set(RisksId.fixedElementsWithBurglaryAndTheft, {
            getInsuranceSumFromResponse: (response) => response.coverages.fixedElementsWithBurglaryAndTheft?.insuranceSum,
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.burglaryAndTheft.fixedElements,
            getInsuranceSumFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.fixedElementsWithBurglaryAndTheft?.insuranceSumInCents
        });
        risks.set(RisksId.risksInOwu, {});
        risks.set(RisksId.devastation, {
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.additionalRisks.devastation,
            getAvailableFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.additionalRisks?.devastation,
            getInsuranceSumFromResponse: (response) => response.coverages.additionalRisks.devastation?.insuranceSum,
            getInsuranceSumFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.additionalRisks?.devastation?.insuranceSumInCents
        });
        risks.set(RisksId.flood, {
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.flood,
            getAvailableFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.flood
        });
        risks.set(RisksId.searchingAndRemovingCauseOfDamage, {
            getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.additionalRisks.searchingAndRemovingCauseOfDamage,
            getAvailableFromRetrievedCalculation: (retrievedCalculation: any) => retrievedCalculation.insurance.additionalRisks?.searchingAndRemovingCauseOfDamage
        });

        return risks;
    }

    private getRisksForPackageMiniForUnderConstructionClosedState() {
        const risks: Map<RisksId, RiskModel> = new Map();
        const defaultMap = this.getRisksForPackageForUnderConstructionClosedState();

        risks.set(RisksId.detachedHouseOrFlatWithFixedElements, {
            ...defaultMap.get(RisksId.detachedHouseOrFlatWithFixedElements),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.detachedHouseOrFlatWithFixedElements}1`, true)
            ]
        });
        risks.set(RisksId.buildingToolsAndMaterials, {
            ...defaultMap.get(RisksId.buildingToolsAndMaterials),
            available: false,
            editable: false
        });
        risks.set(RisksId.fixedElementsWithBurglaryAndTheft, {
            ...defaultMap.get(RisksId.fixedElementsWithBurglaryAndTheft),
            available: false,
            editable: false
        });
        risks.set(RisksId.risksInOwu, {
            ...defaultMap.get(RisksId.risksInOwu),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.risksInOwu}1`, true),
                this.getContextHelp(`${RisksId.risksInOwu}2`, true),
                this.getContextHelp(`${RisksId.risksInOwu}3`, true),
                this.getContextHelp(`${RisksId.risksInOwu}4`, false, "5 000 zł")
            ]
        });

        risks.set(RisksId.devastation, {
            ...defaultMap.get(RisksId.devastation),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.devastation}1`, true),
                this.getContextHelp(`${RisksId.devastation}2`, true),
                this.getContextHelp(`${RisksId.devastation}3`, true)
            ]
        });
        risks.set(RisksId.flood, {
            ...defaultMap.get(RisksId.flood),
            available: false,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.flood}1`, true)
            ]
        });
        risks.set(RisksId.searchingAndRemovingCauseOfDamage, {
            ...defaultMap.get(RisksId.searchingAndRemovingCauseOfDamage),
            available: true,
            editable: false
        });
        return risks;
    }

    private getRisksForPackageMidiForUnderConstructionClosedState() {
        const risks: Map<RisksId, RiskModel> = new Map();
        const defaultMap = this.getRisksForPackageForUnderConstructionClosedState();

        risks.set(RisksId.detachedHouseOrFlatWithFixedElements, {
            ...defaultMap.get(RisksId.detachedHouseOrFlatWithFixedElements),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.detachedHouseOrFlatWithFixedElements}1`, true)
            ]
        });
        risks.set(RisksId.buildingToolsAndMaterials, {
            ...defaultMap.get(RisksId.buildingToolsAndMaterials),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.buildingToolsAndMaterials}1`, true)
            ]
        });
        risks.set(RisksId.fixedElementsWithBurglaryAndTheft, {
            ...defaultMap.get(RisksId.fixedElementsWithBurglaryAndTheft),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.fixedElementsWithBurglaryAndTheft}1`, true),
                this.getContextHelp(`${RisksId.fixedElementsWithBurglaryAndTheft}2`, true)
            ]
        });
        risks.set(RisksId.risksInOwu, {
            ...defaultMap.get(RisksId.risksInOwu),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.risksInOwu}1`, true),
                this.getContextHelp(`${RisksId.risksInOwu}2`, true),
                this.getContextHelp(`${RisksId.risksInOwu}3`, true),
                this.getContextHelp(`${RisksId.risksInOwu}4`, false, "5 000 zł")
            ]
        });


        risks.set(RisksId.devastation, {
            ...defaultMap.get(RisksId.devastation),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.devastation}1`, true),
                this.getContextHelp(`${RisksId.devastation}2`, true),
                this.getContextHelp(`${RisksId.devastation}3`, true)
            ]
        });
        risks.set(RisksId.flood, {
            ...defaultMap.get(RisksId.flood),
            available: false,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.flood}1`, true)
            ]
        });
        risks.set(RisksId.searchingAndRemovingCauseOfDamage, {
            ...defaultMap.get(RisksId.searchingAndRemovingCauseOfDamage),
            available: true,
            editable: false
        });
        return risks;
    }

    private getRisksForPackageMaxiForUnderConstructionClosedState() {
        const risks: Map<RisksId, RiskModel> = new Map();
        const defaultMap = this.getRisksForPackageForUnderConstructionClosedState();

        risks.set(RisksId.detachedHouseOrFlatWithFixedElements, {
            ...defaultMap.get(RisksId.detachedHouseOrFlatWithFixedElements),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.detachedHouseOrFlatWithFixedElements}1`, true)
            ]
        });
        risks.set(RisksId.buildingToolsAndMaterials, {
            ...defaultMap.get(RisksId.buildingToolsAndMaterials),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.buildingToolsAndMaterials}1`, true)
            ]
        });
        risks.set(RisksId.fixedElementsWithBurglaryAndTheft, {
            ...defaultMap.get(RisksId.fixedElementsWithBurglaryAndTheft),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.fixedElementsWithBurglaryAndTheft}1`, true),
                this.getContextHelp(`${RisksId.fixedElementsWithBurglaryAndTheft}2`, true)
            ]
        });
        risks.set(RisksId.risksInOwu, {
            ...defaultMap.get(RisksId.risksInOwu),
            available: true,
            editable: false,
            contextHelp: [
                this.getContextHelp(`${RisksId.risksInOwu}1`, true),
                this.getContextHelp(`${RisksId.risksInOwu}2`, true),
                this.getContextHelp(`${RisksId.risksInOwu}3`, true),
                this.getContextHelp(`${RisksId.risksInOwu}4`, false, "5 000 zł")
            ]
        });
        risks.set(RisksId.devastation, {
            ...defaultMap.get(RisksId.devastation),
            available: true,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.devastation}1`, true),
                this.getContextHelp(`${RisksId.devastation}2`, true),
                this.getContextHelp(`${RisksId.devastation}3`, true)
            ]
        });
        risks.set(RisksId.flood, {
            ...defaultMap.get(RisksId.flood),
            available: false,
            editable: true,
            contextHelp: [
                this.getContextHelp(`${RisksId.flood}1`, true)
            ]
        });
        risks.set(RisksId.searchingAndRemovingCauseOfDamage, {
            ...defaultMap.get(RisksId.searchingAndRemovingCauseOfDamage),
            available: true,
            editable: false
        });
        return risks;
    }
}
